import { Icon, Text } from "@alpacahq/ui/old";

import type { Step } from "~/v2/pages/dashboard/onboarding/session";
import { cva } from "~/v2/utils/cn";

interface ProgressProps {
  steps: Step[];
  reached: number;
}

interface MilestoneProps {
  reached: boolean;
  highlighted: boolean;
  value: number;
  title: string;
  description: string;
}

/**
 * Neither axis decides anything on its own -- a milestone is styled by the pair,
 * which is why both variants are empty and the compounds carry the classes. The
 * base holds the common case (current, or reached-and-current) and each compound
 * names one exception: reached-but-not-current tints the text, and
 * neither-reached-nor-current dims the fill.
 *
 * `reached` and `highlighted` are mutually exclusive at every call site in
 * `Progress` below, so `reached && highlighted` is unreachable in practice. It is
 * still enumerated here rather than left implicit, because that is the value of
 * the table: the combination a reader would have to derive from
 * `reached && !highlighted` is written down.
 */
const milestone = cva(
  "flex h-[40px] min-h-[40px] w-[40px] min-w-[40px] items-center justify-center rounded-lg bg-yellow-300 font-semibold text-black text-sm",
  {
    variants: {
      reached: { true: "", false: "" },
      highlighted: { true: "", false: "" },
    },
    compoundVariants: [
      { reached: true, highlighted: false, class: "text-yellow-900" },
      { reached: false, highlighted: false, class: "bg-yellow-100" },
    ],
  },
);

const Milestone = ({
  highlighted,
  title,
  description,
  reached,
  value,
}: MilestoneProps) => (
  <div className="flex">
    <div className={milestone({ reached, highlighted })}>
      {reached ? <Icon name="Check" /> : value}
    </div>
    <div className="ml-4">
      <Text className="font-medium text-sm">{title}</Text>
      <Text className="text-gray-700 text-sm">{description}</Text>
    </div>
  </div>
);

export const Progress = ({ reached, steps }: ProgressProps) => (
  <div className="flex w-full min-w-[264px] flex-col gap-4">
    {steps.map(({ title, description }, i) => (
      <Milestone
        key={title}
        title={title}
        description={description}
        highlighted={i + 1 === reached}
        reached={i + 1 < reached}
        value={i + 1}
        // @todo check this visually
        //mb={i + 1 < steps.length ? "2rem" : "0"}
      />
    ))}
  </div>
);
